/**
 * Lazy loader + DOM adapter for the product image lightbox.
 *
 * PhotoSwipe 5 lives in its own bundle (assets/wp-lightbox.js) because
 * layout/theme.liquid loads wp-custom.js on every page and the lightbox is only
 * ever needed once someone clicks a product photo. This module is the always-
 * present half: it reads the gallery out of the DOM, pulls the heavy bundle in
 * on demand (prefetching on hover so the click feels instant), and hands over.
 *
 * The click itself is still bound by the vendor assets/theme.js `openGallery`
 * handler, which knows about both product media layouts and the quick-buy
 * container. That handler now delegates here instead of opening PhotoSwipe 4.
 */

import type { GallerySlide } from "../../wp-lightbox/types.js";

const GALLERY_SELECTOR = ".product-detail__images";
const ANCHOR_SELECTOR = "a[data-product-image]";
const BUNDLE_TIMEOUT_MS = 15000;
/** Only used if Liquid somehow failed to emit real dimensions. */
const FALLBACK_IMAGE_SIZE = 2048;

let bundlePromise: Promise<void> | null = null;

function loadLightboxBundle(): Promise<void> {
  if (window.WPLightbox) {
    return Promise.resolve();
  }

  if (bundlePromise) {
    return bundlePromise;
  }

  const src = window.WP_LIGHTBOX_SRC;
  if (!src) {
    return Promise.reject(
      new Error("window.WP_LIGHTBOX_SRC is not set — check layout/theme.liquid"),
    );
  }

  bundlePromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = src;
    script.async = true;

    const timeoutId = window.setTimeout(() => {
      bundlePromise = null;
      reject(new Error("Timed out loading wp-lightbox.js"));
    }, BUNDLE_TIMEOUT_MS);

    script.addEventListener("load", () => {
      window.clearTimeout(timeoutId);
      resolve();
    });

    script.addEventListener("error", () => {
      window.clearTimeout(timeoutId);
      bundlePromise = null;
      script.remove();
      reject(new Error("Failed to load wp-lightbox.js"));
    });

    document.head.appendChild(script);
  });

  return bundlePromise;
}

/**
 * Every gallery anchor, in page order, with Slick's carousel clones removed.
 *
 * The mobile product slideshow duplicates slides for its infinite loop; without
 * this filter those duplicates showed up as repeated images in the lightbox
 * (a latent bug in the PhotoSwipe 4 path, which queried the DOM unfiltered).
 */
function collectAnchors(gallery: Element): HTMLAnchorElement[] {
  const anchors = Array.from(
    gallery.querySelectorAll<HTMLAnchorElement>(ANCHOR_SELECTOR),
  );

  const seenMediaIds = new Set<string>();
  const unique: HTMLAnchorElement[] = [];

  for (const anchor of anchors) {
    if (anchor.closest(".slick-cloned")) {
      continue;
    }

    const mediaId = anchor.dataset.mediaId ?? "";
    if (mediaId.length > 0) {
      if (seenMediaIds.has(mediaId)) {
        continue;
      }
      seenMediaIds.add(mediaId);
    }

    unique.push(anchor);
  }

  return unique;
}

function parseDimension(raw: string | undefined, fallback: number): number {
  const value = Number.parseInt(raw ?? "", 10);
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function buildSlide(anchor: HTMLAnchorElement): GallerySlide {
  const image = anchor.querySelector("img");

  return {
    src: anchor.href,
    srcset: anchor.dataset.pswpSrcset ?? "",
    width: parseDimension(
      anchor.dataset.pswpWidth,
      image?.naturalWidth ?? FALLBACK_IMAGE_SIZE,
    ),
    height: parseDimension(
      anchor.dataset.pswpHeight,
      image?.naturalHeight ?? FALLBACK_IMAGE_SIZE,
    ),
    // The already-decoded page image, shown while the master loads so the open
    // animation has something to zoom from.
    msrc: image?.currentSrc ?? image?.src ?? "",
    alt: image?.alt ?? "",
    element: anchor,
    downloadBasename: anchor.dataset.downloadBasename ?? "product-image",
  };
}

/**
 * Index of the clicked image. A click can land on a Slick clone, which is not
 * in the deduplicated list, so fall back to matching on media id.
 */
function resolveIndex(
  anchors: readonly HTMLAnchorElement[],
  clicked: HTMLAnchorElement | null,
): number {
  if (!clicked) {
    return 0;
  }

  const directIndex = anchors.indexOf(clicked);
  if (directIndex >= 0) {
    return directIndex;
  }

  const mediaId = clicked.dataset.mediaId ?? "";
  if (mediaId.length > 0) {
    const matched = anchors.findIndex(
      (anchor) => anchor.dataset.mediaId === mediaId,
    );
    if (matched >= 0) {
      return matched;
    }
  }

  return 0;
}

async function openFromEvent(event: JQuery.ClickEvent): Promise<void> {
  const clicked = (event.target as HTMLElement | null)?.closest<HTMLAnchorElement>(
    ANCHOR_SELECTOR,
  ) ?? null;

  const gallery =
    clicked?.closest(GALLERY_SELECTOR) ??
    (event.currentTarget as HTMLElement | null)?.closest(GALLERY_SELECTOR) ??
    document.querySelector(GALLERY_SELECTOR);

  if (!gallery) {
    return;
  }

  const anchors = collectAnchors(gallery);
  if (anchors.length === 0) {
    return;
  }

  await loadLightboxBundle();

  const lightbox = window.WPLightbox;
  if (!lightbox) {
    throw new Error("wp-lightbox.js loaded but did not expose window.WPLightbox");
  }

  // Download is a wholesale-account benefit; Liquid decides who qualifies.
  const galleryElement = gallery as HTMLElement;
  const downloadEnabled = galleryElement.dataset.downloadEnabled === "true";
  const loginUrl = galleryElement.dataset.loginUrl ?? "/account/login";

  const syncPageGallery = window.__wpGallerySync;

  lightbox.open({
    slides: anchors.map(buildSlide),
    index: resolveIndex(anchors, clicked),
    downloadEnabled,
    loginUrl,
    onSlideChange: syncPageGallery
      ? (index: number): void => {
          const anchor = anchors[index];
          if (anchor) {
            syncPageGallery($(anchor));
          }
        }
      : undefined,
  });
}

export function initProductLightbox(): void {
  // theme.js's openGallery calls this instead of building a PhotoSwipe 4 gallery.
  window.WPOpenProductLightbox = function handleGalleryClick(
    event: JQuery.ClickEvent,
  ): void {
    void openFromEvent(event).catch((error: unknown) => {
      const message = error instanceof Error ? error.message : String(error);
      console.error(`[wp-custom] Unable to open image lightbox: ${message}`);
    });
  };

  // Warm the bundle on hover so the click itself has nothing to wait for.
  // `mouseenter` rather than `pointerenter` because jQuery only knows how to
  // delegate the former (pointerenter does not bubble). Touch devices skip
  // straight to the click path, which loads on demand.
  $(document).one("mouseenter", ANCHOR_SELECTOR, function prefetchBundle() {
    void loadLightboxBundle().catch(() => {
      // Prefetch is best-effort; the click path reports real failures.
    });
  });
}
